/**
 * Voice service layer types.
 *
 * Mirrors `services/ai/types.ts` exactly: `VoiceProvider` is the seam a
 * future local engine (whisper.cpp + Piper, most likely) implements.
 * `StubVoiceProvider` is the only implementation today — see
 * `VOICE_SYSTEM.md`.
 */

export type VoiceServiceErrorKind = "not-implemented" | "permission-denied" | "no-microphone" | "unknown";

export class VoiceServiceError extends Error {
  readonly kind: VoiceServiceErrorKind;

  constructor(kind: VoiceServiceErrorKind, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "VoiceServiceError";
    this.kind = kind;
  }
}

export interface VoiceInfo {
  readonly id: string;
  readonly name: string;
  readonly language: string;
}

export interface TranscribeRequest {
  /** Raw recorded audio, typically from `MediaRecorder`. */
  audio: Blob;
  language?: string;
  signal?: AbortSignal;
}

export interface SynthesizeRequest {
  text: string;
  voiceId?: string;
  signal?: AbortSignal;
}

/** A chunk of synthesized audio, streamed like `ChatStreamDelta`. */
export interface AudioChunk {
  data: Uint8Array;
  done: boolean;
}

/** The contract a future local STT/TTS engine implements. */
export interface VoiceProvider {
  transcribe(request: TranscribeRequest): Promise<string>;
  synthesize(request: SynthesizeRequest): AsyncGenerator<AudioChunk>;
  listVoices?(): Promise<VoiceInfo[]>;
}
